// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/RuleDetailPanel.test.tsx
import { X } from "@phosphor-icons/react";
import { Badge, Dialog, Flex, IconButton, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { CitationInlinePreview } from "./CitationInlinePreview";
import { SourceViewerDialog } from "./SourceViewerDialog";

import {
  KnowledgeTrustLevel,
  orderCitationsPrimaryFirst,
  type KnowledgeRuleCitationResult,
  type KnowledgeRuleResult,
  type KnowledgeSourceResult,
} from "../../../../../utils/parsers/knowledge";

function citationLocationLabel(citation: KnowledgeRuleCitationResult): string {
  const location = citation.location;
  if (location.cellAnchor?.range) {
    const sheet = location.cellAnchor.sheetName
      ? `${location.cellAnchor.sheetName} · `
      : "";
    return `${sheet}${location.cellAnchor.range}`;
  }
  if (location.pageNumber != null) {
    return `Page ${location.pageNumber}`;
  }
  return "Quote";
}

/**
 * Whether the mini viewer can render this citation inline (a PDF page or a
 * pinned image — the same gate CitationInlinePreview applies). Spreadsheet
 * cell anchors and quote-only citations need the full SourceViewerDialog.
 */
function hasVisualAnchor(citation: KnowledgeRuleCitationResult): boolean {
  const location = citation.location;
  if (location.cellAnchor?.range) return false;
  return (
    location.pageNumber != null ||
    (location.rel_x != null && location.rel_y != null)
  );
}

/**
 * Right-anchored rule detail (deep-linked via ?ruleId=): full text, status,
 * confidence, usage, provenance, and the ranked citations — index 0 is the
 * primary citation (high trust first, then the most recently extracted
 * source, then relevance to the rule). The mini citation viewer renders the
 * SELECTED citation inline as the cited page with its pin (primary by
 * default); clicking a citation card swaps it into the viewer. Citations the
 * mini viewer cannot render (spreadsheet cells, quote-only) open the full
 * source viewer dialog instead.
 */
export function RuleDetailPanel({
  rule,
  sources,
  onClose,
}: {
  rule: KnowledgeRuleResult | undefined;
  sources: KnowledgeSourceResult[];
  onClose: () => void;
}) {
  const [activeCitation, setActiveCitation] =
    useState<KnowledgeRuleCitationResult | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // The panel stays mounted across rule switches (deep-link navigation) —
  // a stale selection would preview the previous rule's citation index.
  const ruleId = rule?._id;
  useEffect(() => {
    setSelectedIndex(0);
  }, [ruleId]);

  if (!rule) return null;

  const sourceName = (sourceId: string) =>
    sources.find((s) => s._id === sourceId)?.file.fileName ?? "Deleted source";

  const citations = orderCitationsPrimaryFirst(rule.citations);
  const selectedCitation = citations[selectedIndex] ?? citations[0];

  return (
    <>
      <Dialog.Root
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <Dialog.Content
          className="fixed right-0 top-0 h-full max-h-full w-[420px] max-w-full overflow-y-auto rounded-none"
          aria-label="Rule detail"
          data-testid="knowledge-rule-detail-panel"
        >
          <Flex justify="between" align="start" className="mb-2">
            <Dialog.Title size="4">Rule</Dialog.Title>
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              aria-label="Close rule detail"
              onClick={onClose}
              data-testid="knowledge-rule-detail-close"
            >
              <X aria-hidden="true" />
            </IconButton>
          </Flex>

          <Flex gap="2" wrap="wrap" className="mb-3">
            <Badge color="gray" variant="soft">
              {rule.status}
            </Badge>
            <Badge color="gray" variant="soft">
              {rule.category}
            </Badge>
            <Badge color="amber" variant="soft">
              {Math.round(rule.confidence * 100)}% confidence
            </Badge>
            {[rule.scope.brand, rule.scope.region, rule.scope.team]
              .filter(Boolean)
              .map((dim) => (
                <Badge key={dim} color="blue" variant="soft">
                  {dim}
                </Badge>
              ))}
          </Flex>

          <Text as="p" size="2" className="mb-3">
            {rule.content}
          </Text>

          <Text
            as="p"
            size="1"
            color="gray"
            className="mb-1"
            data-testid="knowledge-rule-detail-usage"
          >
            {rule.usage.reviewCount > 0
              ? `Used in ${rule.usage.reviewCount} ${
                  rule.usage.reviewCount === 1 ? "review" : "reviews"
                }${
                  rule.usage.lastUsedInReviewAt
                    ? ` · last used ${new Date(
                        rule.usage.lastUsedInReviewAt,
                      ).toLocaleDateString()}`
                    : ""
                }`
              : "Not used in a review yet"}
          </Text>
          <Text as="p" size="1" color="gray" className="mb-3">
            Extracted by Puntt
            {rule.createdAt
              ? ` · ${new Date(rule.createdAt).toLocaleDateString()}`
              : ""}
            {` · confirmed ${rule.confirmationCount} ${
              rule.confirmationCount === 1 ? "time" : "times"
            }`}
          </Text>

          <Text as="p" size="2" weight="bold" className="mb-1">
            Citations
          </Text>
          {selectedCitation ? (
            <div className="mb-2">
              <CitationInlinePreview
                // Remount on selection change so the pdf.js viewer re-opens
                // at the newly selected citation's page instead of keeping
                // the previous scroll/page state.
                key={`${selectedCitation.sourceId}-${selectedIndex}`}
                citation={selectedCitation}
                fileName={sourceName(selectedCitation.sourceId)}
              />
            </div>
          ) : null}
          <Flex direction="column" gap="2">
            {citations.map((citation, index) => (
              <button
                key={`${citation.extractionId ?? citation.sourceId}-${index}`}
                type="button"
                onClick={() => {
                  if (hasVisualAnchor(citation)) {
                    setSelectedIndex(index);
                  } else {
                    setActiveCitation(citation);
                  }
                }}
                aria-label={
                  hasVisualAnchor(citation)
                    ? `View citation ${index + 1} from ${sourceName(
                        citation.sourceId,
                      )} in the preview`
                    : `Open citation ${index + 1} in ${sourceName(
                        citation.sourceId,
                      )}`
                }
                aria-current={index === selectedIndex ? "true" : undefined}
                className={`cursor-pointer rounded border border-solid bg-transparent p-2 text-left ${
                  index === selectedIndex ? "border-blue-8" : "border-gray-5"
                }`}
                data-testid={`knowledge-rule-citation-${rule._id}-${index}`}
              >
                <Flex gap="2" align="center" wrap="wrap" className="mb-1">
                  {index === 0 ? (
                    <Badge color="blue" variant="soft">
                      Primary
                    </Badge>
                  ) : null}
                  <Badge
                    color={
                      citation.trustLevel === KnowledgeTrustLevel.HIGH
                        ? "green"
                        : "gray"
                    }
                    variant="soft"
                  >
                    {citation.trustLevel === KnowledgeTrustLevel.HIGH
                      ? "High trust"
                      : "Low trust"}
                  </Badge>
                  {citation.similarity != null ? (
                    <Badge
                      color="violet"
                      variant="soft"
                      title="How closely this citation matches the rule"
                      data-testid={`knowledge-rule-citation-relevance-${rule._id}-${index}`}
                    >
                      {Math.round(citation.similarity * 100)}% match
                    </Badge>
                  ) : null}
                  <Text size="1" color="gray">
                    {sourceName(citation.sourceId)} ·{" "}
                    {citationLocationLabel(citation)}
                    {citation.extractedAt
                      ? ` · ${new Date(
                          citation.extractedAt,
                        ).toLocaleDateString()}`
                      : ""}
                  </Text>
                </Flex>
                <Text size="1" color="gray" className="italic">
                  “{citation.quote}”
                </Text>
              </button>
            ))}
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <SourceViewerDialog
        citation={activeCitation}
        fileName={
          activeCitation ? sourceName(activeCitation.sourceId) : undefined
        }
        onClose={() => setActiveCitation(null)}
      />
    </>
  );
}
